import {
  Box,
  Checkbox,
  Icon,
  TextField,
  Typography,
} from "@outschool/backpack";
import { FUNDING_OPTION_KEYS } from "@outschool/funding-programs-shared";
import { faArrowRight } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { SelfElectedStateFundingProgram } from "@outschool/ui-auth";
import { TrackedButton } from "@outschool/ui-components-shared";
import { useLinkComponent } from "@outschool/ui-utils";
import React from "react";

export function useStateFundingFormOptions(stateAbbreviation: string) {
  const { t } = useTranslation(
    "client\\components\\banners\\SelfIdentificationPublicFunding",
  );

  const reimbursementFundingOption = {
    topLabel: t("State Program reimbursement"),
    subLabel: t("I submit receipts for reimbursement from my state program."),
    key: FUNDING_OPTION_KEYS.reimbursement,
    activeValue: true,
    inactiveValue: false,
  };

  const programFundingOption = {
    topLabel: t("Education Savings Account"),
    subLabel: t(
      "I use my program funds to purchase classes through my state marketplace.",
    ),
    key: FUNDING_OPTION_KEYS.stateProgramName,
    activeValue: "Education Savings Account",
    inactiveValue: "",
  };

  const debitFundingOption = {
    topLabel: t("Purchase through a state issued debit card"),
    subLabel: t(
      "I use my program issued debit card to purchase Outschool classes.",
    ),
    key: FUNDING_OPTION_KEYS.stateDebitCard,
    activeValue: true,
    inactiveValue: false,
  };

  switch (stateAbbreviation.toUpperCase()) {
    case "AL":
      return {
        fundingOptions: [
          {
            topLabel: t("CHOOSE marketplace"),
            subLabel: t(
              "I use my program funds to purchase classes through my state marketplace.",
            ),
            key: FUNDING_OPTION_KEYS.stateProgramName,
            activeValue: "CHOOSE",
            inactiveValue: "",
          },
          {
            ...reimbursementFundingOption,
            topLabel: t("CHOOSE reimbursement"),
          },
        ],
        isSupportedByVendor: true,
        isSupportedByCharter: true,
      };
    case "AK":
      return {
        fundingOptions: [],
        isSupportedByVendor: true,
        isSupportedByCharter: true,
      };
    case "AZ":
      return {
        fundingOptions: [
          {
            topLabel: t("Empowerment Scholarship Account (ESA) marketplace"),
            subLabel: t(
              "I use my program funds to purchase classes through my state marketplace.",
            ),
            key: FUNDING_OPTION_KEYS.stateProgramName,
            activeValue: "ESA",
            inactiveValue: "",
          },
          {
            topLabel: t("Empowerment Scholarship Account (ESA) reimbursement"),
            subLabel: t(
              "I submit receipts for reimbursement from my state program.",
            ),
            key: FUNDING_OPTION_KEYS.reimbursement,
            activeValue: true,
            inactiveValue: false,
          },
          {
            topLabel: t("Empowerment Scholarship Account (ESA) debit card"),
            subLabel: t(
              "I use my program-issued debit card to purchase classes and/or an Outschool Membership.",
            ),
            key: FUNDING_OPTION_KEYS.stateDebitCard,
            activeValue: true,
            inactiveValue: false,
          },
        ],
        isSupportedByVendor: true,
        isSupportedByCharter: true,
      };
    case "AR":
      return {
        fundingOptions: [
          {
            topLabel: t("Education Freedom Accounts (EFA) marketplace"),
            subLabel: t(
              "I use my program funds to purchase Outschool credits through my state marketplace.",
            ),
            key: "stateProgramName",
            activeValue: "EFA",
            inactiveValue: "",
          },
          {
            ...reimbursementFundingOption,
            topLabel: t("Education Freedom Accounts (EFA) reimbursement"),
          },
        ],
        isSupportedByVendor: true,
        isSupportedByCharter: true,
      };
    case "CA":
      return {
        fundingOptions: [],
        isSupportedByVendor: true,
        isSupportedByCharter: true,
      };
    case "CO":
      return {
        fundingOptions: [
          {
            ...debitFundingOption,
            topLabel: t("Denver MySpark debit card"),
          },
        ],
        isSupportedByVendor: true,
        isSupportedByCharter: true,
      };
    case "FL":
      return {
        fundingOptions: [
          {
            topLabel: t("Florida Scholarship marketplace (EMA)"),
            subLabel: t(
              "I use my program funds to purchase classes through my state marketplace.",
            ),
            key: FUNDING_OPTION_KEYS.stateProgramName,
            activeValue: "EFA",
            inactiveValue: "",
          },
          {
            topLabel: t("Florida Scholarship reimbursement"),
            subLabel: t(
              "I submit receipts for reimbursement from my state program.",
            ),
            key: FUNDING_OPTION_KEYS.reimbursement,
            activeValue: true,
            inactiveValue: false,
          },
        ],
        isSupportedByVendor: true,
        isSupportedByCharter: true,
      };
    case "GA":
      return {
        fundingOptions: [
          {
            ...programFundingOption,
            topLabel: t("GA Promise marketplace"),
            subLabel: t(
              "I use my program funds to purchase Outschool credits through my state marketplace.",
            ),
            activeValue: "GA Promise",
          },
          {
            ...reimbursementFundingOption,
            topLabel: t("GA Promise reimbursement"),
          },
        ],
        isSupportedByVendor: true,
        isSupportedByCharter: true,
      };
    case "HI":
      return {
        fundingOptions: [],
        isSupportedByVendor: true,
        isSupportedByCharter: true,
      };
    case "ID":
      return {
        fundingOptions: [
          {
            ...programFundingOption,
            topLabel: t("Idaho Empowering Parents Program marketplace"),
            subLabel: t(
              "I use my program funds to purchase Outschool credits through my state marketplace.",
            ),
            activeValue: "Idaho Empowering Parent Program",
          },
          {
            ...reimbursementFundingOption,
            topLabel: t("Idaho Empowering Parents program reimbursement"),
          },
        ],
        isSupportedByVendor: true,
        isSupportedByCharter: true,
      };
    case "IN":
    case "IA":
    case "MS":
    case "MO":
    case "MT":
    case "OH":
    case "OK":
    case "OR":
      return {
        fundingOptions: [reimbursementFundingOption],
        isSupportedByVendor: true,
        isSupportedByCharter: true,
      };
    case "WA":
      return {
        fundingOptions: [],
        isSupportedByVendor: true,
        isSupportedByCharter: true,
      };
    case "WV":
      return {
        fundingOptions: [
          {
            topLabel: t("Hope Scholarship marketplace"),
            subLabel: t(
              "I use my program funds to purchase classes through my state marketplace.",
            ),
            key: FUNDING_OPTION_KEYS.stateProgramName,
            activeValue: "Hope",
            inactiveValue: "",
          },
          {
            ...reimbursementFundingOption,
            topLabel: t("Hope Scholarship reimbursement"),
          },
        ],
        isSupportedByVendor: true,
        isSupportedByCharter: true,
      };
    case "NH":
      return {
        fundingOptions: [
          {
            topLabel: t("Education Freedom Accounts (EFA) marketplace"),
            subLabel: t(
              "I use my program funds to purchase classes through my state marketplace.",
            ),
            key: FUNDING_OPTION_KEYS.stateProgramName,
            activeValue: "EFA",
            inactiveValue: "",
          },
          {
            ...reimbursementFundingOption,
            topLabel: t("Education Freedom Account (EFA) reimbursement"),
          },
        ],
        isSupportedByVendor: true,
        isSupportedByCharter: true,
      };
    case "NC":
      return {
        fundingOptions: [
          {
            topLabel: t("ESA+ marketplace"),
            subLabel: t(
              "I use my program funds to purchase classes through my state marketplace.",
            ),
            key: FUNDING_OPTION_KEYS.stateProgramName,
            activeValue: "ESA+",
            inactiveValue: "",
          },
          {
            ...reimbursementFundingOption,
            topLabel: t("ESA+ reimbursement"),
          },
        ],
        isSupportedByVendor: true,
        isSupportedByCharter: true,
      };
    case "SC":
      return {
        fundingOptions: [
          {
            topLabel: t("Education Scholarship Trust Fund (ESTF) marketplace"),
            subLabel: t(
              "I use my program funds to purchase classes through my state marketplace.",
            ),
            key: FUNDING_OPTION_KEYS.stateProgramName,
            activeValue: "ESTF",
            inactiveValue: "",
          },
          {
            ...reimbursementFundingOption,
            topLabel: t(
              "Education Scholarship Trust Fund (ESTF) reimbursement",
            ),
          },
        ],
        isSupportedByVendor: true,
        isSupportedByCharter: true,
      };
    case "TN":
      return {
        fundingOptions: [reimbursementFundingOption],
        isSupportedByVendor: true,
        isSupportedByCharter: true,
      };
    case "UT":
      return {
        fundingOptions: [
          {
            topLabel: t("Utah Fits All (UFA) marketplace"),
            subLabel: t(
              "I use my program funds to purchase Outschool credits through my state marketplace.",
            ),
            key: FUNDING_OPTION_KEYS.stateProgramName,
            activeValue: "UFA",
            inactiveValue: "",
          },
          {
            ...reimbursementFundingOption,
            topLabel: t("Utah Fits All (UFA) reimbursement"),
          },
        ],
        isSupportedByVendor: true,
        isSupportedByCharter: true,
      };
    case "LA":
      return {
        fundingOptions: [
          {
            topLabel: t("LA GATOR marketplace"),
            subLabel: t(
              "I use my program funds to purchase Outschool credits through my state marketplace.",
            ),
            key: FUNDING_OPTION_KEYS.stateProgramName,
            activeValue: "GATOR",
            inactiveValue: "",
          },
          {
            ...reimbursementFundingOption,
            topLabel: t("LA GATOR reimbursement"),
          },
        ],
        isSupportedByVendor: true,
        isSupportedByCharter: true,
      };
    default:
      return {
        fundingOptions: [reimbursementFundingOption],
        isSupportedByVendor: false,
        isSupportedByCharter: false,
      };
  }
}

interface FundingOptionItemProps {
  option: {
    key: string;
    topLabel: string;
    subLabel: string;
    activeValue: any;
    inactiveValue: any;
  };
  isChecked: boolean;
  onToggle: (key: string, value: any) => void;
}

export const FundingOptionItem: React.FC<FundingOptionItemProps> = ({
  option,
  isChecked,
  onToggle,
}) => (
  <Box flex sx={{ flexDirection: "row", gap: 10, mt: 10 }}>
    <Checkbox
      sx={{
        width: "fit-content",
        alignSelf: "flex-start",
        py: 0,
      }}
      id={option.key}
      checked={isChecked}
      onChange={({ target }) => {
        if (target.checked) {
          onToggle(option.key, option.activeValue);
        } else {
          onToggle(option.key, option.inactiveValue);
        }
      }}
    />
    <label
      htmlFor={option.key}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 5,
        cursor: "pointer",
      }}
    >
      <Typography variant="body1">{option.topLabel}</Typography>
      <Typography variant="body2">{option.subLabel}</Typography>
    </label>
  </Box>
);

interface CharterSchoolFundingOptionProps {
  isChecked: boolean;
  onToggle: (key: string, value: boolean) => void;
  t: (key: string) => string;
}

export const CharterSchoolFundingOption: React.FC<
  CharterSchoolFundingOptionProps
> = ({ isChecked, onToggle, t }) => (
  <Box flex sx={{ flexDirection: "row", gap: 10 }}>
    <Checkbox
      sx={{
        width: "fit-content",
        alignSelf: "flex-start",
        py: 0,
      }}
      id="charter"
      checked={isChecked}
      onChange={({ target }) => {
        onToggle(FUNDING_OPTION_KEYS.charter, target.checked);
      }}
    />
    <label
      htmlFor="charter"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 5,
        cursor: "pointer",
      }}
    >
      <Typography variant="body1">{t("Charter School")}</Typography>
      <Typography variant="body2">
        {t("My charter school purchases Outschool enrollments for my learner.")}
      </Typography>
    </label>
  </Box>
);

interface DifferentStateProgramOptionProps {
  isChecked: boolean;
  programName: string;
  onToggle: (key: string, value: string) => void;
  onProgramNameChange: (value: string) => void;
  t: (key: string) => string;
}

export const DifferentStateProgramOption: React.FC<
  DifferentStateProgramOptionProps
> = ({ isChecked, programName, onToggle, onProgramNameChange, t }) => {
  const [isProgramNameInputFocused, setIsProgramNameInputFocused] =
    React.useState<boolean>(false);

  return (
    <Box flex sx={{ flexDirection: "row", gap: 10, mt: 10 }}>
      <Checkbox
        sx={{
          width: "fit-content",
          alignSelf: "flex-start",
          py: 0,
        }}
        id="otherProgramName"
        checked={isChecked || isProgramNameInputFocused}
        onChange={({ target }) => {
          if (!target.checked) {
            // eslint-disable-next-line i18next/no-literal-string
            onToggle("otherProgramName", "");
            setIsProgramNameInputFocused(false);
          } else {
            const input = document.getElementById("otherProgramNameInput");
            if (input) {
              input.focus();
              setIsProgramNameInputFocused(true);
            }
          }
        }}
      />
      <label
        htmlFor="otherProgramName"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 5,
          cursor: "pointer",
          width: "100%",
        }}
      >
        <Typography variant="body1">
          {t("I use a different state program")}
        </Typography>
        <TextField
          id="otherProgramNameInput"
          value={programName}
          size="small"
          placeholder={t("What program do you participate in?")}
          onFocus={() => {
            const checkBox = document.getElementById(
              "otherProgramName",
            ) as HTMLInputElement;
            if (checkBox && !checkBox.checked) {
              setIsProgramNameInputFocused(true);
            }
          }}
          onBlur={({ target }) => {
            if (!target.value) {
              setIsProgramNameInputFocused(false);
            }
          }}
          onChange={({ target }) => {
            onProgramNameChange(target.value);
          }}
          sx={{ width: "100%", backgroundColor: "transparent" }}
        />
      </label>
    </Box>
  );
};

function StateFundingWrapper({
  handleContinue,
  handleSelfPayExit,
  selfElectedStateFundingProgram,
  children,
  setFundingOptions,
  hideButtons = false,
  isSupportedByVendor,
  isSupportedByCharter,
}: {
  handleContinue: () => void;
  handleSelfPayExit: () => void;
  selfElectedStateFundingProgram: SelfElectedStateFundingProgram;
  children: React.ReactNode;
  setFundingOptions: (key: string, value: boolean | string) => void;
  hideButtons?: boolean;
  isSupportedByVendor: boolean;
  isSupportedByCharter: boolean;
}) {
  const { t } = useTranslation(
    "client\\components\\banners\\SelfIdentificationPublicFunding",
  );
  const Link = useLinkComponent();

  const continueDisabled = !Object.values(
    selfElectedStateFundingProgram.fundingOptions,
  ).some(option => !!option);

  return (
    <Box>
      <Typography variant="h4" emphasized>
        {t("Tell us how you’ll fund your learner’s classes")}
      </Typography>
      <Box
        flex
        sx={{
          flexDirection: "column",
          gap: 5,
          mt: 10,
          p: 20,
          backgroundColor: "primary.50",
          borderRadius: 10,
        }}
      >
        <Typography variant="body1">
          {t(
            "Each state has different options for funding your child’s education.",
          )}
        </Typography>
        <Typography variant="body1" emphasized>
          {t("State Funded Programs (ESA, micro-grants, scholarship)")}
        </Typography>
        <Typography variant="body1">
          {t(
            "Selecting a state program helps us personalize your experience, but it doesn’t guarantee coverage.",
          )}
        </Typography>
        <Link
          trackingName="public_funding_self_identification_funding_options_learn_more"
          sx={{
            display: "flex",
            alignItems: "center",
            width: "fit-content",
          }}
          to="/esa"
          target="_blank"
        >
          {t("Learn more")}
          <Icon icon={faArrowRight} sx={{ marginLeft: 5 }} />
        </Link>
        {children}
        {isSupportedByVendor && (
          <DifferentStateProgramOption
            isChecked={
              !!selfElectedStateFundingProgram.fundingOptions.otherProgramName
            }
            programName={
              selfElectedStateFundingProgram.fundingOptions.otherProgramName ||
              ""
            }
            onToggle={setFundingOptions}
            onProgramNameChange={value => {
              // eslint-disable-next-line i18next/no-literal-string
              setFundingOptions("otherProgramName", value);
            }}
            t={t}
          />
        )}
        {isSupportedByCharter && (
          <Box>
            <Typography variant="body1" emphasized sx={{ mt: 10 }}>
              {t("Other funding options")}
            </Typography>
            <CharterSchoolFundingOption
              isChecked={selfElectedStateFundingProgram.fundingOptions.charter}
              onToggle={setFundingOptions}
              t={t}
            />
          </Box>
        )}
      </Box>
      {!hideButtons && (
        <>
          <TrackedButton
            variant="contained"
            trackingName="public_funding_self_identification_funding_options_continue"
            trackingSharedProperties={selfElectedStateFundingProgram}
            onClick={handleContinue}
            disabled={continueDisabled}
            sx={{
              width: "100%",
              mt: 20,
            }}
          >
            {t("Continue")}
          </TrackedButton>
          <TrackedButton
            variant="outlined"
            trackingName="public_funding_self_identification_funding_options_self_pay"
            trackingSharedProperties={selfElectedStateFundingProgram}
            onClick={handleSelfPayExit}
            sx={{
              width: "100%",
              mt: 20,
            }}
          >
            {t("I pay for classes myself")}
          </TrackedButton>
        </>
      )}
    </Box>
  );
}

export function StateFundingForm({
  handleContinue,
  handleSelfPayExit,
  setFundingOptions,
  selfElectedStateFundingProgram,
}: {
  handleContinue: () => void;
  handleSelfPayExit: () => void;
  setFundingOptions: (key: string, value: boolean | string) => void;
  selfElectedStateFundingProgram: SelfElectedStateFundingProgram;
}) {
  const { fundingOptions, isSupportedByVendor, isSupportedByCharter } =
    useStateFundingFormOptions(
      selfElectedStateFundingProgram.stateAbbreviation,
    );

  return (
    <StateFundingWrapper
      handleContinue={handleContinue}
      handleSelfPayExit={handleSelfPayExit}
      selfElectedStateFundingProgram={selfElectedStateFundingProgram}
      setFundingOptions={setFundingOptions}
      isSupportedByVendor={isSupportedByVendor}
      isSupportedByCharter={isSupportedByCharter ?? false}
    >
      {fundingOptions.map(option => (
        <FundingOptionItem
          key={option.key}
          option={option}
          isChecked={
            !!selfElectedStateFundingProgram.fundingOptions[
              option.key as keyof typeof selfElectedStateFundingProgram.fundingOptions
            ]
          }
          onToggle={setFundingOptions}
        />
      ))}
    </StateFundingWrapper>
  );
}
